import React from "react";
import classNames from "classnames";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import type { PickerItemValue } from "@design-system/Picker/Picker.types";
import { TextArea } from "@design-system/TextArea/TextArea";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import gridStyles from "./playgroundControls.module.scss";
import { PlaygroundSelect, type PlaygroundOption } from "./PlaygroundSelect";

export { PlaygroundSelect, type PlaygroundOption } from "./PlaygroundSelect";

/** One-of-many toggle row that never clears: pressing the active option keeps it. */
export const OptionToggle = <Option extends string>({
    options,
    value,
    disabled = false,
    onChange,
}: {
    options: readonly Option[];
    value: Option;
    disabled?: boolean;
    onChange: (value: Option) => void;
}) => (
    <ToggleGroup.Root<Option>
        value={value}
        disabled={disabled}
        onValueChange={(nextValue) => {
            if (nextValue) {
                onChange(nextValue);
            }
        }}
    >
        {options.map((option) => (
            <ToggleGroup.Item key={option} value={option}>
                {option}
            </ToggleGroup.Item>
        ))}
    </ToggleGroup.Root>
);

export const BooleanControl = ({
    label,
    checked,
    disabled = false,
    onChange,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}) => (
    <Checkbox
        label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
    />
);

export const TextControl = ({
    label,
    value,
    onChange,
    placeholder,
    maxLength,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    maxLength?: number;
}) => (
    <Field label={label}>
        <Input
            type="text"
            size="lg"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
        />
    </Field>
);

/**
 * `Input type="number"` on purpose: no design-system number primitive supports
 * `min`/`max`/`step` with decimals (`CountInput` is integer-only,
 * `NumberFormat` is for formatted amounts), and `valueAsNumber` preserves the
 * empty-value behavior the showcases rely on.
 *
 * Clearing the field keeps the last value unless `valueWhenCleared` is given,
 * which is what stories whose raw input used `Number("")` pass as `0`.
 */
export const NumberControl = ({
    label,
    value,
    onChange,
    min,
    max,
    step,
    valueWhenCleared,
}: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    valueWhenCleared?: number;
}) => (
    <label className={gridStyles.field}>
        <span>{label}</span>
        <Input
            type="number"
            size="lg"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(event) => {
                const parsedValue = event.target.valueAsNumber;

                if (Number.isNaN(parsedValue)) {
                    if (valueWhenCleared !== undefined) {
                        onChange(valueWhenCleared);
                    }

                    return;
                }

                onChange(parsedValue);
            }}
        />
    </label>
);

export const SelectControl = <T extends PickerItemValue>({
    label,
    options,
    value,
    onChange,
}: {
    label: string;
    options: ReadonlyArray<PlaygroundOption<T>>;
    value: T;
    onChange: (value: T) => void;
}) => (
    <label className={gridStyles.field}>
        <span>{label}</span>
        <PlaygroundSelect<T>
            options={options}
            value={value}
            onChange={onChange}
            size="sm"
        />
    </label>
);

export const TextareaControl = ({
    label,
    value,
    onChange,
    rows = 4,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    rows?: number;
    placeholder?: string;
}) => (
    <label className={gridStyles.field}>
        <span>{label}</span>
        <TextArea
            size="lg"
            rows={rows}
            value={value}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
        />
    </label>
);

/**
 * Native range input on purpose: the design system has no slider primitive.
 */
export const RangeControl = ({
    label,
    value,
    onChange,
    min,
    max,
    step,
}: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step?: number;
}) => (
    <label className={gridStyles.field}>
        <span>{label}</span>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(event) => onChange(Number(event.target.value))}
        />
    </label>
);

/**
 * Column sizing for a `VariantGrid`.
 * - `auto`: narrow variants, 220px minimum column (the default).
 * - `wide`: variants that need room, 420px minimum column — segmented inputs,
 *   selectable cards, anything the default would crush.
 * - `full`: one variant per row at the full width — tables and page sections.
 * - `three`: exactly three equal columns at any stage width, for small variant
 *   sets that must stay on one row instead of rewrapping with the stage.
 */
export type VariantGridColumns = "auto" | "wide" | "full" | "three";

const variantGridColumnsClassNames: Record<VariantGridColumns, string> = {
    auto: gridStyles.variantGridAuto,
    wide: gridStyles.variantGridWide,
    full: gridStyles.variantGridFull,
    three: gridStyles.variantGridThree,
};

/**
 * @purpose A state family rendered as a section inside a `SandboxLayout`
 * preview column, for secondary components whose whole surface is a small set
 * of fixed states. Anything configurable belongs in the `controls` aside
 * instead, and this must never be a showcase's outermost element.
 */
export const VariantGrid = ({
    title,
    columns = "auto",
    children,
}: {
    title: string;
    columns?: VariantGridColumns;
    children: React.ReactNode;
}) => (
    <section className={gridStyles.variantSection}>
        <h2 className={gridStyles.variantTitle}>{title}</h2>
        <div
            className={classNames(
                gridStyles.variantGrid,
                variantGridColumnsClassNames[columns],
            )}
        >
            {children}
        </div>
    </section>
);

export const VariantItem = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className={gridStyles.variantItem}>
        <div className={gridStyles.variantStage}>{children}</div>
        <code className={gridStyles.variantLabel}>{label}</code>
    </div>
);
